import { useEffect, useState } from 'react';
import { updatePatient } from '../../../../api/patients';
import {
  MEDICAL_CONDITIONS,
  emptyMedicalConditions,
  formatMedicalHistoryNotes,
} from '../../medicalHistory';
import { useToast } from '../../context/ToastContext';
import { ehr } from '../../styles/ehrClasses';
import { fo } from '../../styles/frontOfficeModuleClasses';

function asObject(raw) {
  if (!raw) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
}

function YesNo({ label, value, onChange, details, detailsValue, onDetailsChange, detailsLabel }) {
  return (
    <div className={fo.choiceBox}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="min-w-0 flex-1 text-sm font-semibold text-slate-800">{label}</p>
        <div className="flex items-center gap-4">
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              checked={value === true}
              onChange={() => onChange(true)}
            />
            Yes
          </label>
          <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              checked={value === false}
              onChange={() => onChange(false)}
            />
            No
          </label>
        </div>
      </div>
      {value === true && details ? (
        <label className="mt-3 block">
          <span className={ehr.fieldLabel}>{detailsLabel || 'Details'}</span>
          <input
            className={`${fo.input} mt-1`}
            value={detailsValue || ''}
            onChange={(ev) => onDetailsChange(ev.target.value)}
          />
        </label>
      ) : null}
    </div>
  );
}

/**
 * Correct registration medical-history questionnaire from the medical history book.
 */
export default function EditRegistrationMedicalHistoryModal({ patient, onClose, onSaved }) {
  const { showToast } = useToast();
  const history = asObject(patient?.medical_history) || {};
  const isFemale = patient?.sex === 'female' || patient?.sex === 'f';

  const [form, setForm] = useState(() => ({
    treated_by_doctor: history.treated_by_doctor ?? null,
    treated_by_doctor_for: history.treated_by_doctor_for || '',
    on_medication: history.on_medication ?? null,
    medication_kind: history.medication_kind || '',
    hospitalized: history.hospitalized ?? null,
    hospitalized_when: history.hospitalized_when || '',
    hospitalized_why: history.hospitalized_why || '',
    conditions: { ...emptyMedicalConditions(), ...(history.conditions || {}) },
    other_disease: history.other_disease || '',
    allergy_specify: history.allergy_specify || '',
    pregnant: history.pregnant ?? null,
    pregnant_months: history.pregnant_months || '',
  }));
  const [editReason, setEditReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const h = asObject(patient?.medical_history) || {};
    setForm({
      treated_by_doctor: h.treated_by_doctor ?? null,
      treated_by_doctor_for: h.treated_by_doctor_for || '',
      on_medication: h.on_medication ?? null,
      medication_kind: h.medication_kind || '',
      hospitalized: h.hospitalized ?? null,
      hospitalized_when: h.hospitalized_when || '',
      hospitalized_why: h.hospitalized_why || '',
      conditions: { ...emptyMedicalConditions(), ...(h.conditions || {}) },
      other_disease: h.other_disease || '',
      allergy_specify: h.allergy_specify || '',
      pregnant: h.pregnant ?? null,
      pregnant_months: h.pregnant_months || '',
    });
    setEditReason('');
  }, [patient]);

  if (!patient) return null;

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const reason = editReason.trim();
    if (!reason) {
      showToast('Please explain why you are correcting this record', 'error');
      return;
    }

    const medical_history = {
      treated_by_doctor: form.treated_by_doctor,
      treated_by_doctor_for: form.treated_by_doctor_for.trim() || null,
      on_medication: form.on_medication,
      medication_kind: form.medication_kind.trim() || null,
      hospitalized: form.hospitalized,
      hospitalized_when: form.hospitalized_when.trim() || null,
      hospitalized_why: form.hospitalized_why.trim() || null,
      conditions: { ...(form.conditions || {}) },
      other_disease: form.other_disease.trim() || null,
      allergy_specify: form.allergy_specify.trim() || null,
      pregnant: isFemale ? form.pregnant : null,
      pregnant_months: isFemale ? (String(form.pregnant_months || '').trim() || null) : null,
    };
    medical_history.notes = formatMedicalHistoryNotes({
      ...medical_history,
      medical_aid_name: patient.medical_aid_name,
      membership_number: patient.membership_number,
      sex: patient.sex,
    });

    setSaving(true);
    try {
      await updatePatient(patient.id, {
        medical_history,
        edit_reason: reason,
      });
      showToast('Medical history corrected. Previous and new values are on file.', 'success');
      await onSaved?.();
      onClose?.();
    } catch (err) {
      showToast(err.message || 'Failed to update medical history', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/50 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-reg-history-title"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget && !saving) onClose?.();
      }}
    >
      <form
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:p-6"
        onSubmit={handleSubmit}
      >
        <h2 id="edit-reg-history-title" className={ehr.panelTitle}>
          Correct medical history
        </h2>
        <p className="mt-1 text-sm text-slate-600">
          Correct the registration medical history questionnaire. The previous answers stay on file
          with your name and reason.
        </p>

        <div className="mt-4 space-y-3">
          <YesNo
            label="Treated by a doctor?"
            value={form.treated_by_doctor}
            onChange={(v) => setField('treated_by_doctor', v)}
            details
            detailsLabel="Treated for"
            detailsValue={form.treated_by_doctor_for}
            onDetailsChange={(v) => setField('treated_by_doctor_for', v)}
          />
          <YesNo
            label="On medication?"
            value={form.on_medication}
            onChange={(v) => setField('on_medication', v)}
            details
            detailsLabel="Medication"
            detailsValue={form.medication_kind}
            onDetailsChange={(v) => setField('medication_kind', v)}
          />
          <YesNo
            label="Ever hospitalized?"
            value={form.hospitalized}
            onChange={(v) => setField('hospitalized', v)}
          />
          {form.hospitalized === true ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block">
                <span className={ehr.fieldLabel}>When</span>
                <input
                  className={`${fo.input} mt-1`}
                  value={form.hospitalized_when}
                  onChange={(ev) => setField('hospitalized_when', ev.target.value)}
                />
              </label>
              <label className="block">
                <span className={ehr.fieldLabel}>Why</span>
                <input
                  className={`${fo.input} mt-1`}
                  value={form.hospitalized_why}
                  onChange={(ev) => setField('hospitalized_why', ev.target.value)}
                />
              </label>
            </div>
          ) : null}

          <div className={fo.choiceBox}>
            <p className="text-sm font-semibold text-slate-800">Conditions</p>
            <div className="mt-3 space-y-2">
              {MEDICAL_CONDITIONS.map((row) => (
                <div key={row.key} className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-slate-700">{row.label}</span>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-1.5 text-xs">
                      <input
                        type="radio"
                        checked={form.conditions?.[row.key] === true}
                        onChange={() =>
                          setForm((prev) => ({
                            ...prev,
                            conditions: { ...prev.conditions, [row.key]: true },
                          }))
                        }
                      />
                      Yes
                    </label>
                    <label className="inline-flex items-center gap-1.5 text-xs">
                      <input
                        type="radio"
                        checked={form.conditions?.[row.key] === false}
                        onChange={() =>
                          setForm((prev) => ({
                            ...prev,
                            conditions: { ...prev.conditions, [row.key]: false },
                          }))
                        }
                      />
                      No
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <label className="block">
            <span className={ehr.fieldLabel}>Other disease</span>
            <input
              className={`${fo.input} mt-1`}
              value={form.other_disease}
              onChange={(ev) => setField('other_disease', ev.target.value)}
            />
          </label>
          <label className="block">
            <span className={ehr.fieldLabel}>Allergy details</span>
            <input
              className={`${fo.input} mt-1`}
              value={form.allergy_specify}
              onChange={(ev) => setField('allergy_specify', ev.target.value)}
            />
          </label>

          {isFemale ? (
            <YesNo
              label="Pregnant?"
              value={form.pregnant}
              onChange={(v) => setField('pregnant', v)}
              details
              detailsLabel="Months"
              detailsValue={form.pregnant_months}
              onDetailsChange={(v) => setField('pregnant_months', v)}
            />
          ) : null}

          <label className="block">
            <span className={ehr.fieldLabel}>Reason for correction</span>
            <textarea
              className={`${fo.textarea} mt-1`}
              value={editReason}
              onChange={(ev) => setEditReason(ev.target.value)}
              rows={2}
              required
            />
          </label>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-2">
          <button type="button" className={fo.btnOutline} onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className={fo.btnPrimary} disabled={saving}>
            {saving ? 'Saving…' : 'Save correction'}
          </button>
        </div>
      </form>
    </div>
  );
}
